import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../redux/hooks";
import { createVoucher, fetchTenants, fetchVoucher } from "../redux/voucherActions";
import { clearError } from "../redux/voucherReducer";
import Footer from "../components/footer/Footer";
import ErrorPopup from '../components/ErrorPopup/ErrorPopup';
import VoucherFormCard from "../components/card/VoucherFormCard";
import VoucherDetailCard from "../components/card/VoucherDetailCard";
import Navbar from "../components/navbar/Navbar";
import Accordion from "../components/accordian/Accordion";
import parse from 'html-react-parser';
import { Download, CreditCard, ShieldCheck, ScanLine, Smartphone, Wallet, QrCode } from 'lucide-react';

const ICICI_CBDC_APP_URL = 'https://play.google.com/store/apps/details?id=com.icici.digitalrupee&hl=en';

// ICICI CBDC's "how to redeem" flow - fundamentally different from eRupi's
// Google Pay/UPI-voucher-tab flow above: redemption happens entirely inside
// a dedicated app, via Aadhaar-linked SIM + No-KYC verification, not an
// OTP+PIN voucher activation. Icons are placeholders (lucide-react, already
// a dependency) until branded screenshots/icons are provided - see step
// cards below for where to swap them in.
function CbdcStepCard({ step, icon, text }: { step: number; icon: React.ReactNode; text: string }) {
  return (
    <div className="relative">
      <div
        className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-28 h-14 bg-[#53978E] rounded-b-full flex items-center justify-center z-10 backdrop-blur-sm"
        style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25), 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' }}
      >
        <span className="text-white font-bold text-3xl">{step}</span>
      </div>
      <div className="bg-white bg-opacity-30 backdrop-blur-xs border-opacity-100 rounded-xl p-6 shadow-lg h-56 w-56 relative z-30" style={{ borderWidth: '1.5px' }}>
        <div className="flex flex-col items-center text-center h-full justify-center pt-3">
          <div className="w-9 h-9 my-4 flex items-center justify-center text-[#53978e]">
            {icon}
          </div>
          <p className="text-sm font-medium" style={{ color: '#0A0908' }}>
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}

function CbdcStepArrow() {
  return (
    <>
      <div className="md:hidden flex items-center justify-center my-4">
        <img
          src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/down%20arrow.png"
          alt="Down Arrow"
          className="w-8 h-10"
        />
      </div>
      <div className="hidden md:flex items-center justify-center">
        <img
          src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/arrow%20right.png"
          alt="Arrow"
          className="w-15 h-10 mx-2"
        />
      </div>
    </>
  );
}

function HowToUseCbdc() {
  return (
    <div className="flex flex-col gap-6 mt-12 mx-auto items-center">
      <div className="flex flex-col md:flex-row items-center justify-center gap-4">
        <CbdcStepCard step={1} icon={<Download className="w-6 h-6" />} text="Download and install the Digital Rupee by ICICI Bank app" />
        <CbdcStepArrow />
        <CbdcStepCard step={2} icon={<ShieldCheck className="w-6 h-6" />} text="Verify your SIM using the mobile number linked to your Aadhaar" />
        <CbdcStepArrow />
        <CbdcStepCard step={3} icon={<CreditCard className="w-6 h-6" />} text="Complete a quick No-KYC verification by entering your Aadhaar number" />
        <CbdcStepArrow />
        <CbdcStepCard step={4} icon={<ScanLine className="w-6 h-6" />} text='Open your Special Purpose Digital Rupee Wallet and tap "Scan and Pay" to spend' />
      </div>
      <a
        href={ICICI_CBDC_APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block px-6 py-3 text-sm font-semibold text-white bg-[#53978e] rounded-lg hover:bg-[#43786f] transition-colors"
      >
        Download Digital Rupee by ICICI Bank
      </a>
    </div>
  );
}

// MYP (DuitNow QR) "how to redeem" flow - shown instead of the Google Pay /
// UPI-voucher steps, which only apply to the India eRupi processors.
function HowToUseMyp() {
  return (
    <div className="flex flex-col gap-6 mt-12 mx-auto items-center">
      <div className="flex flex-col md:flex-row items-center justify-center gap-4">
        <CbdcStepCard step={1} icon={<Smartphone className="w-6 h-6" />} text="Open the MYP wallet app and sign in with the mobile number you entered" />
        <CbdcStepArrow />
        <CbdcStepCard step={2} icon={<Wallet className="w-6 h-6" />} text="Your voucher will be shown in your wallet" />
        <CbdcStepArrow />
        <CbdcStepCard step={3} icon={<QrCode className="w-6 h-6" />} text="At a participating merchant, scan their DuitNow QR code" />
        <CbdcStepArrow />
        <CbdcStepCard step={4} icon={<CreditCard className="w-6 h-6" />} text="Confirm the amount and enter your PIN to pay" />
      </div>
    </div>
  );
}

const loadingOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(5px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

function CreateVoucherPage(): JSX.Element | null {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { voucher, loading, error, tenants, tenantsLoading, tenantsError, lastSaralCode, lastPin } = useAppSelector((state: any) => state.voucher);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [checkStatusLoading, setCheckStatusLoading] = useState(false);

  const handleCheckStatus = async (): Promise<void> => {
    if (!lastSaralCode || !lastPin) return;
    setCheckStatusLoading(true);
    try {
      await dispatch(fetchVoucher({ saral_code: lastSaralCode, pin: lastPin })).unwrap();
    } catch (error) {
      // Error is handled by Redux and displayed in ErrorPopup
    } finally {
      setCheckStatusLoading(false);
    }
  };

  const handleSubmit = async (name: string, number: string): Promise<void> => {
    if (!voucher?.saralId || !voucher?.token) {
      console.error('SaralId or token is missing from voucher');
      return;
    }
    dispatch(
      createVoucher({
        saralId: voucher.saralId,
        token: voucher.token,
        user_details: {
          name: name,
          number: number,
        }
      })
    );
  };

  // Success navigation handled in route guard

  useEffect(() => {
    if (error) {
      setShowErrorPopup(true);
    }
  }, [error]);

  useEffect(() => {
    if (!voucher?.token || !voucher?.roleResourceId) {
      return;
    }
    const programId = voucher?.program?.programId || voucher?.programId;
    if (programId && voucher.roleResourceId) {
      dispatch(fetchTenants({ 
        programId, 
        token: voucher.token, 
        roleResourceId: voucher.roleResourceId 
      }));
    }
  }, [dispatch, voucher]);

  const handlePopupClose = (): void => {
    setShowErrorPopup(false);
    dispatch(clearError());
    // On any backend error, return user to home to restart flow
    navigate('/', { replace: true });
  };

  const handleScrollToOutlets = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const outletsElement = document.getElementById('outlets');
    if (outletsElement) {
      const elementPosition = outletsElement.offsetTop;
      const offsetPosition = elementPosition - 300; // 100px offset from top

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (voucher === null) {
    return null;
  }

  const accordian_items = []
  if (!tenantsLoading && !tenantsError && tenants && tenants.length > 0) {
    accordian_items.push({
      title: "Participating Outlets",
      content: (
        <div id="outlets" className="w-full">
          {tenantsLoading && (
            <div className="py-4 text-gray-500">Loading locations...</div>
          )}
          {tenantsError && (
            <div className="py-4 text-red-600">{tenantsError}</div>
          )}
          {!tenantsLoading && !tenantsError && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              {tenants && tenants.length > 0 ? (
                tenants.map((t: any) => (
                  <div key={t.tenantId} className="bg-white border border-gray-200 rounded-xl overflow-hidden flex items-center p-3">
                    <div className="w-12 h-12  rounded-lg flex-shrink-0">
                      {t.storeLogo ? (
                        <img src={t.storeLogo} alt={t.tenantBrand || 'Outlet'} className="h-full object-contain rounded-lg" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 rounded-lg">No Logo</div>
                      )}
                    </div>
                    <div className="flex-1 flex items-center  px-4">
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {t.tenantBrand || 'Outlet Name'}
                        </div>

                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-gray-500">No locations available.</div>
              )}
            </div>
          )}
        </div>
      ),
    })
  }

  accordian_items.push({
    title: "Terms & Conditions",
    content: voucher.program.standardTerms,
  }
  )

  const isCbdc = voucher.brand === 'ICICI_CBDC';
  const isMyp = voucher.brand === 'MYP';

  accordian_items.push(
    {
      title: "How to use Voucher",
      content: isCbdc ? (
        <HowToUseCbdc />
      ) : isMyp ? (
        <HowToUseMyp />
      ) : (
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-12 mx-auto">
          {/* Card 1 */}
          <div className="relative">
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-28 h-14 bg-[#53978E]  rounded-b-full flex items-center justify-center z-10 backdrop-blur-sm" style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25), 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' }}>
              <span className="text-white font-bold text-3xl">1</span>
            </div>
            <div className="bg-white bg-opacity-30 backdrop-blur-xs  border-opacity-100 rounded-xl p-6 shadow-lg h-56 w-56 relative z-30" style={{ borderWidth: '1.5px' }}>
              <div className="flex flex-col items-center text-center h-full justify-center pt-3">
                <img
                  src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/app%20logo.png"
                  alt="Google Pay"
                  className="w-9 h-9 my-4"
                />
                <p className="text-sm font-medium" style={{ color: '#0A0908' }}>
                  Open Google Pay and go to the profile section
                </p>
              </div>
            </div>
          </div>

          {/* Mobile Down Arrow 1 */}
          <div className="md:hidden flex items-center justify-center my-4">
            <img
              src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/down%20arrow.png"
              alt="Down Arrow"
              className="w-8 h-10"
            />
          </div>

          {/* Arrow 1 */}
          <div className="hidden md:flex items-center justify-center">
            <img
              src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/arrow%20right.png"
              alt="Arrow"
              className="w-15 h-10 mx-2"
            />
          </div>

          {/* Card 2 */}
          <div className="relative">
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-28 h-14 bg-[#53978E]  rounded-b-full flex items-center justify-center z-10 backdrop-blur-sm" style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25), 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' }}>
              <span className="text-white font-bold text-3xl">2</span>
            </div>
            <div className="bg-white bg-opacity-30 backdrop-blur-xs  border-opacity-100 rounded-xl p-6 shadow-lg h-56 w-56 relative z-30" style={{ borderWidth: '1.5px' }}>
              <div className="flex flex-col items-center text-center h-full justify-center pt-3">
                <img
                  src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/gift%20logo.png"
                  alt="Voucher"
                  className="w-9 h-9 my-4"
                />
                <p className="text-sm font-medium" style={{ color: '#0A0908' }}>
                  Navigate to UPI vouchers, select your voucher and activate
                </p>
              </div>
            </div>
          </div>

          {/* Mobile Down Arrow 2 */}
          <div className="md:hidden flex items-center justify-center my-4">
            <img
              src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/down%20arrow.png"
              alt="Down Arrow"
              className="w-8 h-10"
            />
          </div>

          {/* Arrow 2 */}
          <div className="hidden md:flex items-center justify-center">
            <img
              src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/arrow%20right.png"
              alt="Arrow"
              className="w-15 h-10 mx-2"
            />
          </div>

          {/* Card 3 */}
          <div className="relative">
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-28 h-14 bg-[#53978E]  rounded-b-full flex items-center justify-center z-10 backdrop-blur-sm" style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25), 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' }}>
              <span className="text-white font-bold text-3xl">3</span>
            </div>
            <div className="bg-white bg-opacity-30 backdrop-blur-xs  border-opacity-100 rounded-xl p-6 shadow-lg h-56 w-56 relative z-30" style={{ borderWidth: '1.5px' }}>
              <div className="flex flex-col items-center text-center h-full justify-center pt-3">
                <img
                  src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/otp%20logo.png"
                  alt="OTP"
                  className="w-9 h-9 my-4"
                />
                <p className="text-sm font-medium" style={{ color: '#0A0908' }}>
                  Activate your Voucher using the OTP received from the issuer, then set your PIN to use it.

                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  )




  return (
    <div className="h-[80vh] flex flex-col" style={{
      backgroundImage: 'url(https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/gingham%20bg.png), linear-gradient(180deg, rgba(221, 236, 235, 1) 50%, rgba(221, 236, 235, 0) 100%)',
      backgroundSize: '2000px, cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'repeat, no-repeat',
    }}>
      {loading && (
        <div className="loading-overlay" style={loadingOverlayStyle}>
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#53978e] border-t-transparent"></div>
            <p className="text-gray-600 font-medium">Processing your request...</p>
          </div>
        </div>
      )}
      <div className="flex-grow">

        <Navbar />
        {/* Hero Section */}
        <div className="flex flex-col items-center justify-center py-12 px-4 md:px-0">
          {/* <div className="md:w-32 md:h-32 w-24 h-24 bg-white rounded-lg shadow-md flex items-center justify-center mb-6">
            <img src={voucher.program.logo} alt="Program Logo" className="w-24 h-24 object-contain rounded-lg" />
          </div> */}
          <h1 className="md:text-5xl text-center  text-3xl font-bold bg-gradient-to-r from-[#1b312e] to-[#53978e] text-transparent bg-clip-text mb-4">
            {voucher.program.displayName}
          </h1>
          <div className="text-lg text-gray-600 text-center max-w-2xl">
            {parse(voucher.program.description, {
              replace: (domNode: any) => {
                if (domNode.name === 'a' && domNode.attribs?.href === '#outlets') {
                  return (
                    <a
                      href="#outlets"
                      onClick={handleScrollToOutlets}
                      className="cta-link text-[#53978e]  cursor-pointer"
                    >
                      {domNode.children?.[0]?.data || 'The list of outlets is below.'}
                    </a>
                  );
                }
              }
            })}
          </div>
        </div>
        <div className="container mx-auto px-8 lg:px-8 py-6 sm:py-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="flex flex-col  justify-end items-center">
            <VoucherDetailCard voucher={voucher} />
          </div>
          <div className="flex flex-col justify-end items-center">
            <VoucherFormCard
              handleSubmit={handleSubmit}
              voucher={voucher}
              onCheckStatus={handleCheckStatus}
              checkStatusLoading={checkStatusLoading}
            />
          </div>

        </div>

        <div className="container mx-auto px-8 lg:px-14 py-6 sm:py-8">
          <div className="flex flex-col justify-end items-center">


            <Accordion
              items={accordian_items}
            />



            <div className="text-2xl text-center font-medium text-gray-600 mt-16">
              <div className="block md:inline">Got Questions?</div>
              <div className="block md:inline md:ml-2"><a className="text-[#53978e] cursor-pointer" href="https://fincentive.co/#contact" >Contact Our Help Center</a></div>
            </div>
          </div>
        </div>



      </div>
      <Footer />
      {showErrorPopup && (
        <ErrorPopup
          message={error}
          onClose={handlePopupClose}
        />
      )}
    </div>
  );
}

export default CreateVoucherPage; 